import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/AppShell";
import { ADMIN_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Filter, XCircle } from "lucide-react";

const ACTION_OPTIONS = [
  { value: "__all", label: "All actions" },
  { value: "login", label: "Login" },
  { value: "logout", label: "Logout" },
  { value: "punch_in", label: "Punch in" },
  { value: "punch_out", label: "Punch out" },
  { value: "break_start", label: "Break start" },
  { value: "break_end", label: "Break end" },
  { value: "dealership_tag_posted", label: "Dealership tag posted" },
  { value: "customer_inspection_posted", label: "Customer inspection posted" },
  { value: "assessment_submit", label: "Assessment submitted" },
  { value: "policy_signed", label: "Policy signed" },
  { value: "id_proof_uploaded", label: "ID uploaded" },
  { value: "employee_invited", label: "Employee invited" },
  { value: "employee_terminated", label: "Employee terminated" },
  { value: "employee_reinstated", label: "Employee reinstated" },
  { value: "weekly_report_sent", label: "Weekly report sent" },
  { value: "weekly_report_auto_sent", label: "Weekly report auto-sent" },
];

const ACTION_STYLES = {
  login: "text-green-400", logout: "text-zinc-400",
  punch_in: "text-[#FF3B30]", punch_out: "text-amber-400",
  break_start: "text-amber-400", break_end: "text-emerald-400",
  dealership_tag_posted: "text-blue-400", customer_inspection_posted: "text-purple-400",
  assessment_submit: "text-emerald-400", policy_signed: "text-cyan-400",
  employee_invited: "text-indigo-400", employee_terminated: "text-red-400",
  employee_reinstated: "text-green-400",
  weekly_report_sent: "text-[#FF3B30]", weekly_report_auto_sent: "text-[#FF3B30]",
};

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({ user_id: "__all", action: "__all", start: "", end: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {});
    load();
  }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (f.user_id && f.user_id !== "__all") params.user_id = f.user_id;
      if (f.action && f.action !== "__all") params.action = f.action;
      if (f.start) params.start = new Date(f.start).toISOString();
      if (f.end) params.end = new Date(f.end + "T23:59:59").toISOString();
      const { data } = await api.get("/admin/activity-logs", { params });
      setLogs(data);
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  function clearFilters() {
    setF({ user_id: "__all", action: "__all", start: "", end: "" });
    setTimeout(load, 0);
  }

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (f.user_id && f.user_id !== "__all") c++;
    if (f.action && f.action !== "__all") c++;
    if (f.start) c++;
    if (f.end) c++;
    return c;
  }, [f]);

  return (
    <AppShell nav={ADMIN_NAV} title="Activity Logs" subtitle="Audit trail">
      <div className="brand-surface rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-overline"><Filter size={12} />Filters {activeFilterCount > 0 && <span className="ml-1 text-[#FF3B30]">· {activeFilterCount} active</span>}</div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-zinc-500 hover:text-white flex items-center gap-1" data-testid="logs-clear-filters"><XCircle size={12} />Clear</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Employee</Label>
            <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
              <SelectTrigger className="h-10 bg-[#0A0A0A] border-white/10 mt-1" data-testid="logs-filter-employee"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white max-h-80">
                <SelectItem value="__all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`logs-employee-option-${e.id}`}>{e.name} <span className="text-zinc-500">· {e.email}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Action</Label>
            <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}>
              <SelectTrigger className="h-10 bg-[#0A0A0A] border-white/10 mt-1" data-testid="logs-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white max-h-80">
                {ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px] uppercase text-zinc-500">From</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="h-10 bg-[#0A0A0A] border-white/10 mt-1" data-testid="logs-filter-start" /></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">To</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="h-10 bg-[#0A0A0A] border-white/10 mt-1" data-testid="logs-filter-end" /></div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={load} className="bg-[#FF3B30] hover:bg-[#E02920] h-10 px-6" data-testid="logs-apply-filters">Apply</Button>
          <div className="ml-auto text-xs text-zinc-500 self-center">{logs.length} events</div>
        </div>
      </div>

      <div className="brand-surface rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-overline text-left">
              <th className="py-3 px-4">Time</th>
              <th className="py-3 px-4">User</th>
              <th className="py-3 px-4">Action</th>
              <th className="py-3 px-4">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="4" className="py-10 text-center text-zinc-500">Loading…</td></tr>)}
            {!loading && logs.length === 0 && (<tr><td colSpan="4" className="py-10 text-center text-zinc-500">No activity matches these filters.</td></tr>)}
            {!loading && logs.map((l) => (
              <tr key={l.id} className="border-t border-white/5" data-testid={`log-row-${l.id}`}>
                <td className="py-3 px-4 text-zinc-400 font-mono text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="py-3 px-4 font-semibold">{l.user_name || "—"}</td>
                <td className={`py-3 px-4 font-mono text-xs ${ACTION_STYLES[l.action] || "text-zinc-300"}`}>{l.action}</td>
                <td className="py-3 px-4 text-zinc-500 max-w-md truncate">{Object.keys(l.meta || {}).length > 0 ? JSON.stringify(l.meta) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </AppShell>
  );
}
